import React from "react";
import { Check } from "lucide-react";

const PLANS = [
  {
    name: "System Support Plan",
    price: "$497",
    cadence: "/month",
    bestFor: "Clients who already have a system and need maintenance.",
    features: [
      "CRM support",
      "Automation monitoring",
      "Form testing",
      "Minor funnel updates",
      "Basic reporting",
      "Small workflow / copy updates",
    ],
    cta: "Keep My System Running",
  },
  {
    name: "Revenue Optimization Plan",
    price: "$997",
    cadence: "/month+",
    bestFor: "Businesses that want ongoing improvement and revenue support.",
    features: [
      "Monthly funnel review",
      "Lead source review",
      "Campaign performance review",
      "Conversion improvements",
      "Reactivation campaigns",
      "New automation adjustments",
      "Offer / CTA improvements",
      "Monthly performance call",
    ],
    cta: "Optimize My Revenue System",
  },
];

export default function MonthlySupport({ onCtaClick, tone = "light" }) {
  const dark = tone === "dark";
  const handleCta = () => {
    if (onCtaClick) onCtaClick();
    else window.dispatchEvent(new Event("open-audit"));
  };

  return (
    <section
      data-testid="monthly-support-section"
      className={`py-20 sm:py-24 md:py-28 px-5 sm:px-6 md:px-10 ${
        dark ? "bg-[#0F0F0E]" : ""
      }`}
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center reveal">
          <p className={`eyebrow ${dark ? "!text-[#A56A43]" : ""}`}>Ongoing Optimization</p>
          <h2 className={`font-display mt-5 text-2xl sm:text-3xl md:text-4xl leading-tight tracking-[-0.02em] font-semibold ${
            dark ? "text-[#F4F0E8]" : "text-ink"
          }`}>
            Keep the system running.
            <span className={`italic font-normal ${dark ? "text-[#BEB8AF]" : "text-ink-muted"}`}> Improve it over time.</span>
          </h2>
          <p className={`mt-4 text-[15px] md:text-base max-w-xl mx-auto leading-relaxed ${
            dark ? "text-[#BEB8AF]" : "text-ink-muted"
          }`}>
            Once your system is live, ongoing support keeps the workflow clean,
            tested, and improving.
          </p>
        </div>

        <div className="mt-10 sm:mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          {PLANS.map((p, idx) => (
            <article
              key={p.name}
              data-testid={`monthly-card-${idx}`}
              className={`reveal rounded-[32px] border p-6 sm:p-7 md:p-8 transition-colors flex flex-col ${
                dark
                  ? "border-[#3C3935] bg-[#151514] hover:border-[#A56A43]"
                  : "border-sand-300 bg-transparent hover:bg-sand-50"
              }`}
            >
              <div className="flex items-center justify-between">
                <h3 className={`font-display text-lg font-medium tracking-tight ${dark ? "text-[#F4F0E8]" : "text-ink"}`}>
                  {p.name}
                </h3>
                <span className={`font-mono text-[10px] uppercase tracking-[0.28em] ${dark ? "text-[#8E887F]" : "text-ink-muted"}`}>
                  · monthly
                </span>
              </div>
              <div className="mt-4 flex items-baseline gap-1 flex-wrap">
                <span className={`font-display text-4xl font-semibold tracking-[-0.02em] ${dark ? "text-[#F4F0E8]" : "text-ink"}`}>
                  {p.price}
                </span>
                <span className={`text-sm ${dark ? "text-[#8E887F]" : "text-ink-muted"}`}>{p.cadence}</span>
              </div>
              <p className={`mt-3 text-[13.5px] leading-relaxed ${dark ? "text-[#BEB8AF]" : "text-ink-muted"}`}>
                {p.bestFor}
              </p>
              <ul className={`mt-6 grid grid-cols-1 gap-2.5 flex-1 border-t pt-5 ${dark ? "border-[#33302D]" : "border-sand-300"}`}>
                {p.features.map((f) => (
                  <li
                    key={f}
                    className={`flex items-start gap-3 text-[14px] leading-relaxed ${dark ? "text-[#F4F0E8]" : "text-ink"}`}
                  >
                    <span className={`mt-0.5 h-5 w-5 grid place-items-center rounded-full shrink-0 ${
                      dark ? "bg-[#B1744B]/15 text-[#B1744B]" : "bg-sand-200 text-olive"
                    }`}>
                      <Check size={12} strokeWidth={2.4} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={handleCta}
                data-testid={`monthly-cta-${idx}`}
                className={`mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[14px] font-medium transition-colors min-h-[48px] ${
                  dark
                    ? "bg-transparent border-[1.5px] border-[#3C3935] text-[#F4F0E8] hover:bg-[#1A1A18] hover:border-[#A56A43]"
                    : "border border-ink-deep text-ink hover:bg-sand-200"
                }`}
              >
                {p.cta}
              </button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
